import { ContractorDocument } from './contractor-document.model';

/**
 * A contractor's state background check — a credential verified *manually* by a
 * business administrator, distinct from the automated Checkr flow modelled by
 * `BackgroundCheckResponse`.
 *
 * Audience shapes the payload. A business admin receives every field; a facility
 * receives everything but `verifiedByUserId`; the contractor receives their own
 * record without `notes`, which is admin-authored commentary about them rather
 * than for them.
 *
 * The API serializes with `DefaultIgnoreCondition = WhenWritingNull`, so fields
 * the caller is not entitled to arrive **undefined rather than null** — test for
 * presence, never for null. "Not for you" and "not set" are deliberately
 * indistinguishable, so never render a "none yet" empty state off an absent
 * field.
 */
export interface StateBackgroundCheck {
  id: string;
  contractorId: string;

  /** Whether an administrator has confirmed the state check. */
  isVerified: boolean;

  /**
   * Date the administrator verified the check, entered by hand. ISO date string.
   * Independent of when the record was created or last edited.
   */
  verifiedDate?: string | null;

  /**
   * Free text written by a business administrator and shown to every facility
   * viewing this professional. Omitted for contractor-audience responses.
   */
  notes?: string | null;

  documentId?: string | null;

  /** The supporting document the contractor uploaded, when there is one. */
  document?: ContractorDocument | null;

  /** The admin who last set the verification. Business-admin audience only. */
  verifiedByUserId?: string | null;

  createdAt?: string;
  updatedAt?: string;
}

/** Business-admin write payload. */
export interface UpdateStateBackgroundCheckRequest {
  isVerified: boolean;
  verifiedDate?: string | null;
  notes?: string | null;
  documentId?: string | null;
}
